import React, { Fragment, MouseEvent, ChangeEvent } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { DateTime } from "luxon";

import { V2 } from "../../Api";
import withTitle from "../withTitle";
import { formatDateTimeLocal, angleToDirection, formatDateTimeDurationLong, formatName, formatDateLocal, formatNumber, formatUSD } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import Rights from "../Rights";

import ViewToolbar from "../ViewToolbar";
import ToolbarMenuItem from "../ToolbarMenuItem";
import Indicator from "../Indicator";
import NoteBox from "../NoteBox";
import HasRight from "../HasRight";
import OverflowMenuItem from "../OverflowMenuItem";

import VehicleAddServiceDialog from "../dialogs/VehicleAddServiceDialog";
import VehicleAddPaymentDialog from "../dialogs/VehicleAddPaymentDialog";
import VehicleUpdateMileageDialog from "../dialogs/VehicleUpdateMileageDialog";
import FileUploadDialog from "../FileUploadDialog";


interface IVehicleDetailsViewProps extends RouteComponentProps<any>, IAuthProps {

}

interface IVehicleDetailsViewState {
    isLoading: boolean | string;
    vehicle: any;
    vehicleService?: any;
    vehicleValue?: any;
    notes?: any[];

    noteFilter: string;
    newNoteType: string;

    isAddServiceDialogOpen: boolean;
    addServiceDialogMode: "full" | "nextservice";
    isAddPaymentDialogOpen: boolean;
    addPaymentType: "tag" | "insurance" | "kbb" | "purchaseprice";
    isMileageUpdateDialogOpen: boolean;
    isUploadDialogOpen: boolean;

    preventUpload: boolean;
    receiptTotal: string;
    receiptGallons: string;
    receiptVendor: string;
    receiptNote: string;
    uploadExtra: any;
}

class VehicleDetailsView extends React.Component<IVehicleDetailsViewProps, IVehicleDetailsViewState> {
    constructor(props: Readonly<IVehicleDetailsViewProps>) {
        super(props);

        this.state = {
            isLoading: true,
            vehicle: {},

            noteFilter: "",
            newNoteType: "0",

            isAddServiceDialogOpen: false,
            addServiceDialogMode: "full",
            isAddPaymentDialogOpen: false,
            addPaymentType: "tag",
            isMileageUpdateDialogOpen: false,
            isUploadDialogOpen: false,

            preventUpload: true,
            receiptTotal: "",
            receiptGallons: "",
            receiptVendor: "",
            receiptNote: "",
            uploadExtra: {}
        };

        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.handleReceiptItemChanged = this.handleReceiptItemChanged.bind(this);
        this.toggleAddServiceDialog = this.toggleAddServiceDialog.bind(this);
        this.toggleAddPaymentDialog = this.toggleAddPaymentDialog.bind(this);
        this.toggleMileageDialog = this.toggleMileageDialog.bind(this);
        this.genericNoteRenderExtra = this.genericNoteRenderExtra.bind(this);
        this.newNoteRenderExtra = this.newNoteRenderExtra.bind(this);
        this.addNote = this.addNote.bind(this);
        this.deleteNote = this.deleteNote.bind(this);
        this.toggleUploadDialog = this.toggleUploadDialog.bind(this);
        this.receiptUploadSuccess = this.receiptUploadSuccess.bind(this);
    }

    public componentDidMount() {
        this.loadVehicleInfo()
            .then(() => {
                this.loadServiceInfo();
                this.loadCostInfo();
                this.loadNotes();
            });
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }

        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-text">{this.state.isLoading}</div>
            </div>;
        }

        return <div className="viewer-root">
            <ViewToolbar>
                <ToolbarMenuItem right="veh:write">
                    <Link to={{ pathname: this.props.location.pathname + "/edit" }} className="icon-pencil" title="Edit vehicle..." />
                </ToolbarMenuItem>
                <OverflowMenuItem>
                    <ToolbarMenuItem right="veh:write">
                        <button type="button" onClick={this.toggleUploadDialog}><i className="icon-upload" /><span>Upload receipt...</span></button>
                    </ToolbarMenuItem>
                </OverflowMenuItem>
            </ViewToolbar>
            <VehicleAddServiceDialog isOpen={this.state.isAddServiceDialogOpen} onRequestClose={this.toggleAddServiceDialog} vehicle={this.state.vehicle} mode={this.state.addServiceDialogMode} />
            <VehicleAddPaymentDialog isOpen={this.state.isAddPaymentDialogOpen} onRequestClose={() => this.toggleAddPaymentDialog(true)} vehicle={this.state.vehicle} paymentType={this.state.addPaymentType} />
            <VehicleUpdateMileageDialog isOpen={this.state.isMileageUpdateDialogOpen} onRequestClose={() => this.toggleMileageDialog(true)} vehicle={this.state.vehicle} />
            <FileUploadDialog isOpen={this.state.isUploadDialogOpen} title="Upload Receipt" extraData={this.state.uploadExtra} preventUpload={this.state.preventUpload} closeOnSuccess={true} accept="image/jpeg, image/png" descriptionOverride="Gas receipt" overrideCompression="image" endpoint={`vehicles/${this.props.match.params.id}/receipts`} onRequestClose={this.toggleUploadDialog} onUploadSuccess={this.receiptUploadSuccess}>
                <Fragment>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="receiptTotal">Total Amount</label>
                            <input type="number" name="receiptTotal" value={this.state.receiptTotal} min={0} step={0.01} onChange={this.handleReceiptItemChanged} />
                        </div>
                        <div className="input-form-group">
                            <label htmlFor="receiptGallons">Gallons</label>
                            <input type="number" name="receiptGallons" value={this.state.receiptGallons} min={0} step={0.001} onChange={this.handleReceiptItemChanged} />
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="receiptVendor">Vendor</label>
                            <input type="text" name="receiptVendor" value={this.state.receiptVendor} onChange={this.handleReceiptItemChanged} />
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="receiptNote">Note <small>(optional)</small></label>
                            <textarea name="receiptNote" value={this.state.receiptNote} onChange={this.handleReceiptItemChanged} />
                        </div>
                    </section>
                </Fragment>
            </FileUploadDialog>
            <div className="column-root">
                <section className="info-block">
                    <header>
                        <h2>Basic Info</h2>
                    </header>
                    <dl className="info-list">
                        <div className="info-list-row">
                            <div className="col-50">
                                <dt>Tag</dt>
                                <dd>{this.state.vehicle.tag}</dd>
                            </div>
                            <div className="col-50">
                                <dt>VIN</dt>
                                <dd>{this.state.vehicle.vin}</dd>
                            </div>
                        </div>
                        <div className="info-list-row">
                            <div className="col-75">
                                <dt>Make & Model</dt>
                                <dd>{this.state.vehicle.name}</dd>
                            </div>
                            <div className="col-25">
                                <dt>Color</dt>
                                <dd>{this.state.vehicle.color || "N/A"}</dd>
                            </div>
                        </div>
                        <div className="info-list-row">
                            <div className="col-75">
                                <dt>Status</dt>
                                {this.state.vehicle.checkedOut
                                    ? <>
                                        <dd>Checked out by {this.state.vehicle.checkedOut.user.name}</dd>
                                        <dd className="small-text">Since {formatDateLocal(this.state.vehicle.checkedOut.timestamp)}</dd>
                                    </>
                                    : <dd>Available</dd>
                                }
                            </div>
                            <div className="col-25">
                                <dt>Usage</dt>
                                <dd>{this.vehicleStatusToUsage(this.state.vehicle.status)}</dd>
                            </div>
                        </div>
                    </dl>
                </section>
                <section className="info-block">
                    <header>
                        <h2>Extra Info</h2>
                    </header>
                    <dl className="info-list">
                        <div className="info-list-row">
                            <div>
                                <div className="info-list-row">
                                    <div className="col-50">
                                        <dt>Odometer</dt>
                                        <dd>{this.state.vehicle.tracker
                                            ? `${Math.round(this.state.vehicle.tracker.lastOdometer).toLocaleString()} miles`
                                            : this.state.vehicle.manualOdometer?.updated
                                                ? `${Math.round(this.state.vehicle.manualOdometer.odometer).toLocaleString()} miles`
                                                : "Not available"}</dd>
                                        <dd className="small-text">As of {this.state.vehicle.tracker
                                            ? formatDateLocal(this.state.vehicle.tracker.lastUpdate)
                                            : this.state.vehicle.manualOdometer?.updated
                                                ? formatDateLocal(this.state.vehicle.manualOdometer.updated)
                                                : "Unknown"}</dd>
                                        {!this.state.vehicle.tracker
                                            ? <HasRight right={Rights.vehicle.write}>
                                                <dd>
                                                    <button type="button" className="button btn-outline btn-narrow text-with-icon nowrap" onClick={() => this.toggleMileageDialog()}><i className="icon-pencil" /><span>Update mileage</span></button>
                                                </dd>
                                            </HasRight>
                                            : null}
                                    </div>
                                    <div className="col-50">
                                        <dt>Fuel</dt>
                                        <dd>{this.state.vehicle.tracker?.lastFuel ? `${this.state.vehicle.tracker.lastFuel} %` : "Not Available"}</dd>
                                    </div>
                                </div>
                                
                            </div>
                        </div>
                        {this.state.vehicle.tracker
                            ? <div className="info-list-row">
                                <div className="col-50">
                                    <dt>Last seen</dt>
                                    <dd>{this.vehicleTrackerToIndicator(this.state.vehicle.tracker)}</dd>
                                    <dd>{this.vehicleTrackerToBatteryIndicator(this.state.vehicle.tracker)}</dd>
                                </div>
                            </div>
                            : null}
                    </dl>
                </section>
            </div>
            &nbsp;
            <div className="column-root">
                {/*<table className="table-vertical section-table">
                    <thead>
                        <tr>
                            <th className="col-smallest">Timestamp</th>
                            <th className="col-smallest">User</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Vehicle logs</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>11/24/2020, 11:45 AM EDT</td>
                            <td>Calista Hernandez</td>
                            <td>Checked out</td>
                        </tr>
                        {/*this.state.log.length === 0
                            ? <td colSpan={10}>No log entries available</td>
                            : this.state.log.map(a => <tr key={a.timestamp}>
                                <td data-caption="Timestamp: ">{formatDateTimeLocalNumeric(a.timestamp)}</td>
                                <td data-caption="User: ">{a.userName}</td>
                                <td data-caption="Action: ">{this.actionToString(a.action)}</td>
                            </tr>)* /}
                    </tbody>
                </table>*/}
                <HasRight right={Rights.vehicle.read}>
                    <section className="info-block">
                        <header>
                            <h2>Servicing</h2>
                        </header>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Last service</dt>
                                    <dd>{formatDateLocal(this.state.vehicleService?.history?.[0]?.dateServiced) || "Unknown date"}</dd>
                                    <dd>{formatNumber(this.state.vehicleService?.history?.[0]?.mileage) || "Unknown"} miles</dd>
                                </div>
                                <div>
                                    <dt>Next service</dt>
                                    <dd>{formatDateLocal(this.state.vehicleService?.nextServiceDate) || "Not specified"}</dd>
                                    <dd>{formatNumber(this.state.vehicleService?.nextServiceMileage) || "N/A"} miles</dd>
                                    <dd>{this.vehicleServiceToPastDueIndicator(this.state.vehicle, this.state.vehicleService)}</dd>
                                </div>
                            </div>
                        </dl>
                        <HasRight right={Rights.vehicle.write}>
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button type="button" className="button text-with-icon nowrap" onClick={() => this.toggleAddServiceDialog(false, "full")}><i className="icon-plus" /><span>Add service</span></button>
                                <button type="button" className="button btn-outline text-with-icon nowrap" onClick={() => this.toggleAddServiceDialog(false, "nextservice")}><i className="icon-pencil" /><span>Update next service</span></button>
                            </div>
                        </HasRight>
                        <h4>Service history</h4>
                        <NoteBox notes={this.state.vehicleService?.history || []} hideAuthor={true} hideControls={true} noteHeaderExtraRender={this.serviceNoteRenderExtra} />
                    </section>
                </HasRight>
                <HasRight right={Rights.vehicle.read}>
                    <section className="info-block">
                        <header>
                            <h2>Costs & Value</h2>
                        </header>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Original Purchase Price</dt>
                                    <dd>{formatUSD(this.state.vehicleValue?.price?.purchasePrice)}</dd>
                                    <dd><small>{formatDateLocal(this.state.vehicleValue?.price?.purchaseDate)}</small></dd>
                                    {this.state.vehicleValue?.price?.purchasePrice === null
                                        ? <HasRight right={Rights.vehicle.write}>
                                            <dd>
                                                <button type="button" className="button btn-outline btn-narrow text-with-icon nowrap" onClick={() => this.toggleAddPaymentDialog(false, "purchaseprice")}><i className="icon-pencil" /><span>Set purchase price</span></button>
                                            </dd>
                                        </HasRight>
                                        : null}
                                </div>
                                <div>
                                    <dt>Kelly Blue Book Value</dt>
                                    <dd>{formatUSD(this.state.vehicleValue?.price?.kbbValue)}</dd>
                                    <dd><small>As of {formatDateLocal(this.state.vehicleValue?.price?.kbbDate) || "N/A"}</small></dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Last Tag Renewal</dt>
                                    <dd>{formatDateLocal(this.state.vehicleValue?.tagTax?.history?.[0]?.renewalDate) || "Unknown"}</dd>
                                    <dd>{formatUSD(this.state.vehicleValue?.tagTax?.history?.[0]?.cost)}</dd>
                                </div>
                                <div>
                                    <dt>Next Tag Renewal</dt>
                                    <dd>{formatDateLocal(this.state.vehicleValue?.tagTax?.nextTagRenewalDate) || "Unknown"}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Last Insurance Payment</dt>
                                    <dd>{formatDateLocal(this.state.vehicleValue?.insurance?.history?.[0]?.date) || "None entered"}</dd>
                                    <dd>{this.renderInsurancePayment(this.state.vehicleValue?.insurance)}</dd>
                                </div>
                            </div>
                        </dl>
                        <HasRight right={Rights.vehicle.write}>
                            &nbsp;
                            <div className="action-panel-dialog wrap">
                                <button type="button" className="button btn-outline btn-narrow text-with-icon nowrap" onClick={() => this.toggleAddPaymentDialog(false, "kbb")}><i className="icon-pencil" /><span>Update KBB Value</span></button>
                                <button type="button" className="button btn-outline btn-narrow text-with-icon nowrap" onClick={() => this.toggleAddPaymentDialog(false, "tag")}><i className="icon-plus" /><span>Add Tag Renewal</span></button>
                                <button type="button" className="button btn-outline btn-narrow text-with-icon nowrap" onClick={() => this.toggleAddPaymentDialog(false, "insurance")}><i className="icon-plus" /><span>Add Insurance Payment</span></button>
                            </div>
                        </HasRight>
                        <h4>Total Costs</h4>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Insurance</dt>
                                    <dd>{formatUSD(this.state.vehicleValue?.totals.insurance)}</dd>
                                </div>
                                <div>
                                    <dt>Tag</dt>
                                    <dd>{formatUSD(this.state.vehicleValue?.totals.tagTax)}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Service</dt>
                                    <dd>{formatUSD(this.state.vehicleValue?.totals.service)}</dd>
                                </div>
                                <div>
                                    <dt>Gas</dt>
                                    <dd>{formatUSD(this.state.vehicleValue?.totals.gas)}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Total - KBB</dt>
                                    <dd>{formatUSD(this.state.vehicleValue?.totals.sellCost)}</dd>
                                </div>
                                <div>
                                    <dt><b>Total Vehicle Cost</b></dt>
                                    <dd><b>{formatUSD(this.state.vehicleValue?.totals.total)}</b></dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                </HasRight>
            </div>
            &nbsp;
            <div className="column-root">
                <section className="info-block">
                    <header>
                        <h2>Notes</h2>
                    </header>

                    <select id="frm-note-group" name="noteFilter" value={this.state.noteFilter} onChange={e => this.handleInputChanged(e, this.loadNotes)} placeholder="" autoComplete="off_please_chrome">
                        <option value="">All</option>
                        <option value="0">General</option>
                        <option value="1">Accident</option>
                        <option value="2">Repairs</option>
                        <option value="3">Repairs needed</option>
                    </select>
                    &nbsp;
                    {!this.state.notes?.length
                        ? <span className="text-light">No notes found</span>
                        : null
                    }
                    <NoteBox notes={this.state.notes || []} hideControls={!this.props.authContext.hasRight(Rights.vehicle.noteCreate)} noteHeaderExtraRender={this.genericNoteRenderExtra} newNoteHeaderExtraRender={this.newNoteRenderExtra} displayName={this.props.authContext.user.displayName} onNewNote={this.addNote} allowDeletion={this.props.authContext.hasRight(Rights.vehicle.noteDelete)} onNoteDelete={this.deleteNote} allowUpload={true} />
                </section>
                <section />
            </div>
        </div>;
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, callback?: () => void) {
        let name = e.currentTarget.name;
        let value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            hasChanged: true,

            [name]: value
        } as any, callback);
    }

    private handleReceiptItemChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        } as any, () => {
            const preventUpload = isNaN(parseFloat(this.state.receiptTotal)) || isNaN(parseFloat(this.state.receiptGallons)) || !this.state.receiptVendor.trim();

            this.setState({
                preventUpload,
                uploadExtra: {
                    note: this.state.receiptNote,
                    total: this.state.receiptTotal,
                    gallons: this.state.receiptGallons,
                    vendor: this.state.receiptVendor,
                }
            });
        });
    }

    private async loadVehicleInfo() {
        try {
            const vehicle = await V2.get<any>(`vehicles/${this.props.match.params.id}`);

            const checkedOutKey = vehicle.keys.find((a: any) => a.userId);
            vehicle.checkedOut = checkedOutKey;

            this.setState({
                isLoading: false,
                vehicle
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load vehicle information"
            });
        };
    }

    private async loadServiceInfo() {
        if (!this.props.authContext.hasRight(Rights.vehicle.read)) {
            return;
        }

        try {
            // Load service info
            const vehicleService = await V2.get<any>(`vehicles/${this.props.match.params.id}/service`);

            if (vehicleService?.history) {
                for (const history of vehicleService?.history) {
                    history.body = history.note || <span className="text-light small-text">No note entered</span>;
                    history.timestamp = history.dateServiced;
                }
            }

            this.setState({
                isLoading: false,
                vehicleService
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load vehicle service information"
            });
        }

    }

    private async loadCostInfo() {
        if (!this.props.authContext.hasRight(Rights.vehicle.read)) {
            return;
        }

        try {
            // Load cost & value info
            const vehicleValue = await V2.get<any>(`vehicles/${this.props.match.params.id}/costvalue`);

            this.setState({
                isLoading: false,
                vehicleValue
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load vehicle cost & value information"
            });
        }
    }

    private async loadNotes() {
        if (!this.props.authContext.hasRight(Rights.vehicle.read)) {
            return;
        }

        try {
            const parameters = [];
            if (this.state.noteFilter) {
                parameters.push(`group=${this.state.noteFilter}`);
            }

            // Load vehicle notes
            const notes = await V2.get<any>(`vehicles/${this.props.match.params.id}/notes${parameters.length > 0 ? `?${parameters.join("&")}` : ""}`);

            for (const note of notes) {
                note.author = formatName(note.employee.name);
            }

            this.setState({
                isLoading: false,
                notes
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load vehicle notes"
            });
        }
    }

    private serviceNoteRenderExtra(note: any) {
        return <div>
            <span>{formatDateLocal(note.dateServiced)}</span>
            <br />
            <span>Odometer: <b>{note.mileage ? `${note.mileage.toLocaleString()} miles` : "Not specified"}</b></span>
            <HasRight right={Rights.vehicle.read}>
                <br />
                <span>Cost: <b>{note.cost ? `$${(+note.cost).toFixed(2)}` : "Not specified"}</b></span>
            </HasRight>
        </div>;
    }

    private genericNoteRenderExtra(note: any) {
        return <div>
            <span>{formatDateTimeLocal(note.timestamp)}</span>
            <br />
            <span>Type: <b>{this.noteGroupToText(note.group)}</b></span>
        </div>;
    }

    private newNoteRenderExtra() {
        return <select id="frm-new-note-group" name="newNoteType" value={this.state.newNoteType} onChange={this.handleInputChanged} placeholder="" autoComplete="off_please_chrome">
            <option value="0">General</option>
            <option value="1">Accident</option>
            <option value="2">Repairs</option>
            <option value="3">Repairs needed</option>
        </select>;
    }

    private noteGroupToText(group: number) {
        switch (group) {
            case 0: return "General";
            case 1: return "Accident";
            case 2: return "Repairs";
            case 3: return "Repairs needed";
            default: return `Unknown (${group})`;
        }
    }

    private vehicleStatusToUsage(status: number) {
        switch (status) {
            case 0: return "Hidden";
            case 1: return "Enabled";

            default: return "Unknown (" + status + ")";
        }
    }

    private trackerLocationToLink(tracker: any) {
        if (!tracker.lastPosition) {
            return "Unknown";
        }

        let displayName = `${tracker.lastPosition.x.toFixed(6)}, ${tracker.lastPosition.y.toFixed(6)}`;
        let bubbleText = "";

        if (tracker.lastAddress && (!tracker.geofence || tracker.geofence.locationId === "0")) {
            displayName = tracker.lastAddress.replace(/, \d+$/, "");
        }
        else if (tracker.geofence) {
            displayName = tracker.geofence.name;
            bubbleText = +tracker.geofence.locationId ? "Office" : "";
        }

        return <a href={`https://maps.google.com/?t=k&q=${tracker.lastPosition.x},${tracker.lastPosition.y}`} onClick={this.openMapsWindow}>
            {bubbleText ? <Indicator type="slim" color="blue" text="Office" /> : null}
            {displayName}
        </a>;
    }

    private openMapsWindow(e: MouseEvent<HTMLAnchorElement>) {
        e.stopPropagation();
        e.preventDefault();

        window.open(e.currentTarget.href, undefined, "resizable,scrollbars,status");
    }

    private vehicleTrackerToIndicator(tracker: any) {
        if (!tracker) {
            return null;
        }

        return tracker.lastIsActive === true && tracker.lastSpeed
            ? <Fragment><Indicator type="slim" color="green">Moving {angleToDirection(tracker.lastHeading)} @ {tracker.lastSpeed === null ? "?" : Math.round(tracker.lastSpeed / 1.60934)} MPH</Indicator> near {this.trackerLocationToLink(tracker)}</Fragment>
            : tracker.lastIsActive === true
                ? <Fragment><Indicator type="slim" color="yellow">Idle for {tracker.lastMovement ? formatDateTimeDurationLong(tracker.lastMovement) : "N/A"}</Indicator> near {this.trackerLocationToLink(tracker)}</Fragment>
                : <Fragment>Parked for {tracker.lastMovement ? formatDateTimeDurationLong(tracker.lastMovement) : "N/A"} near {this.trackerLocationToLink(tracker)}</Fragment>
    }

    private vehicleTrackerToBatteryIndicator(tracker: any) {
        if (!tracker) {
            return null;
        }

        return tracker.lastVoltage
            ? tracker.lastVoltage < 11.65
                ? <div><Indicator color="red" type="slim" icon="warning">Battery voltage critical ({tracker.lastVoltage.toFixed(1)} V)</Indicator></div>
                : tracker.lastVoltage <= 11.9
                    ? <div><Indicator color="orange" type="slim" icon="warning">Battery voltage low ({tracker.lastVoltage.toFixed(1)} V)</Indicator></div>
                    : <div><Indicator color="lightblue" type="slim" icon="check">Battery voltage OK ({tracker.lastVoltage.toFixed(1)} V)</Indicator></div>
            : null;
    }

    private vehicleServiceToPastDueIndicator(vehicle: any, service: any) {
        if (service?.nextServiceDate && DateTime.fromISO(service.nextServiceDate) < DateTime.local()) {
            return <Indicator type="slim" color="yellow" icon="warning" text="Past due" />;
        }
        if (service?.nextServiceMileage && vehicle?.tracker?.lastOdometer && vehicle.tracker.lastOdometer > service?.nextServiceMilage) {
            return <Indicator type="slim" color="yellow" icon="warning" text="Past due" />;
        }
    }

    private toggleAddServiceDialog(requestRefresh?: boolean, mode?: "full" | "nextservice") {
        this.setState(s => ({
            addServiceDialogMode: mode || s.addServiceDialogMode,
            isAddServiceDialogOpen: !s.isAddServiceDialogOpen
        }));

        if (requestRefresh === true) {
            this.loadServiceInfo();
        }
    }

    private toggleAddPaymentDialog(requestRefresh?: boolean, type?: "tag" | "insurance" | "kbb" | "purchaseprice") {
        this.setState(s => ({
            addPaymentType: type || s.addPaymentType,
            isAddPaymentDialogOpen: !s.isAddPaymentDialogOpen
        }));

        if (requestRefresh === true) {
            this.loadCostInfo();
        }
    }

    private toggleMileageDialog(requestRefresh = false) {
        this.setState(s => ({
            isMileageUpdateDialogOpen: !s.isMileageUpdateDialogOpen
        }));

        if (requestRefresh === true) {
            this.loadVehicleInfo();
        }
    }

    private async addNote(body: string, attachments?: string[]) {
        if (!body) {
            throw new Error("Notes must have a body");
        }

        const data = {
            body: body,
            group: this.state.newNoteType || 0,
            attachments
        };

        const note = await V2.post<any>(`vehicles/${this.state.vehicle.id}/notes`, data);

        if (attachments?.length) {
            // Handle server not giving us the file metadata after creating the note with an attachment
            this.loadNotes();
        }
        else {
            const notes = this.state.notes;
            note.author = formatName(note.employee.name);

            notes?.push(note);

            this.setState({
                notes
            });
        }
    }

    private async deleteNote(index: number) {
        const notes = this.state.notes;
        const note = notes?.[index];
        if (!note) {
            return;
        }

        await V2.delete(`vehicles/${this.state.vehicle.id}/notes/${note.id}`);

        notes?.splice(index, 1);

        this.setState({
            notes
        });
    }

    private renderInsurancePayment(insurance: any) {
        if (!insurance) {
            return "-";
        }

        const diff = (+insurance?.history?.[0]?.cost - +insurance?.history?.[1]?.cost) || 0;
        if (diff > 0)
            return <>{formatUSD(insurance?.history?.[0]?.cost)} (Up {formatUSD(diff)})</>;
        else if (diff < 0)
            return <>{formatUSD(insurance?.history?.[0]?.cost)} (Down {formatUSD(diff)})</>;
        else if (diff === 0 && insurance?.history?.length > 1)
            return <>{formatUSD(insurance?.history?.[0]?.cost)} (No change)</>;
        else
            return <>{formatUSD(insurance?.history?.[0]?.cost)}</>;
    }

    private toggleUploadDialog() {
        const { isUploadDialogOpen } = this.state;

        this.setState({
            isUploadDialogOpen: !isUploadDialogOpen
        });
    }

    private receiptUploadSuccess(ids: string[]) {
        this.loadCostInfo();
    }
}

export default withTitle("Vehicle Details", withAuth(VehicleDetailsView));
